import React, { useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import {
  DropZone,
  Heading,
  H2,
  IconBackArrow,
  IconRightArrow,
  IconSpinner,
  Icons,
  PrimaryButton,
  Reviews,
} from '../../components';

import css from './ListingPage.module.css';
import { isArrayLength } from '../../util/genericHelpers';
import Slider from 'react-slick';
import { createPublicListingReview } from '../../util/api';

const MAX_PUBLIC_REVIEW_PHOTO_SIZE_BYTES = 20 * 1024 * 1024;
function RenderNextArrow(props) {
  const { className, onClick } = props;
  return (
    <div className={className} onClick={onClick}>
      <IconRightArrow className={css.icon} />
    </div>
  );
}

function RenderPrevArrow(props) {
  const { className, onClick } = props;
  return (
    <div className={className} onClick={onClick}>
      <IconBackArrow className={css.icon} />
    </div>
  );
}
function mergeReviews(reviews, imageReviews) {
  return reviews.map(review => {
    // Find all matching imageReviews based on content and rating
    const matchingImageReviews = isArrayLength(imageReviews)
      ? imageReviews.filter(
          imageReview =>
            imageReview?.content === review?.attributes?.content &&
            imageReview?.rating === review?.attributes?.rating
        )
      : [];

    // If matches are found, combine the images with the review object
    if (matchingImageReviews?.length) {
      // Extract all images from the matching imageReviews
      const images = isArrayLength(matchingImageReviews)
        ? matchingImageReviews.map(imageReview => imageReview.image)
        : [];

      // Concatenate all found images to the review object
      return {
        ...review,
        images: review.images ? review.images.concat(images) : images,
      };
    }

    // If no match is found, return the review without modification
    return review;
  });
}

const hasReviewedPublicListing = (reviews, currentUser) => {
  const currentUserId = currentUser?.id?.uuid;
  return Boolean(
    currentUserId &&
      reviews.find(review => review?.publicListingReview && review?.authorId === currentUserId)
  );
};

const PublicListingReviewForm = props => {
  const {
    listing,
    reviews,
    currentUser,
    hasVisibleReviews,
    onUploadPublicReviewImage,
    onReviewCreated,
  } = props;
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [error, setError] = useState(null);

  const listingId = listing?.id?.uuid;
  const alreadyReviewed = hasReviewedPublicListing(reviews, currentUser);
  if (!listingId || alreadyReviewed) return null;

  const validateFile = file => {
    if (!file?.type?.startsWith('image/')) return 'Review photos must be image files.';
    if (file.size > MAX_PUBLIC_REVIEW_PHOTO_SIZE_BYTES) {
      return 'Review photos must be 20 MB or smaller.';
    }
    return null;
  };

  const onAttachmentUpload = async file => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploadInProgress(true);
    setError(null);
    try {
      const url = await onUploadPublicReviewImage?.(file);
      if (!url) throw new Error('Photo upload failed.');
      setPhotos(existingPhotos => [
        ...existingPhotos,
        {
          url,
          name: file.name,
          contentType: file.type,
          size: file.size,
        },
      ]);
    } catch (uploadError) {
      setError(uploadError?.message || 'Photo upload failed.');
    } finally {
      setUploadInProgress(false);
    }
  };

  const onSubmit = async event => {
    event.preventDefault();
    if (uploadInProgress) return;
    if (!rating) {
      setError('Choose a rating before submitting your review.');
      return;
    }
    if (!comment.trim()) {
      setError('Write a short review before submitting.');
      return;
    }

    setSubmitInProgress(true);
    setError(null);
    try {
      const response = await createPublicListingReview({
        listingId,
        rating,
        comment: comment.trim(),
        photos,
      });
      setRating(0);
      setComment('');
      setPhotos([]);
      onReviewCreated?.(response?.review);
    } catch (submitError) {
      setError(
        submitError?.error?.message ||
          submitError?.message ||
          'Your review could not be saved. Please try again.'
      );
    } finally {
      setSubmitInProgress(false);
    }
  };

  const formClasses = hasVisibleReviews
    ? `${css.publicListingReviewForm} ${css.publicListingReviewFormAfterReviews}`
    : css.publicListingReviewForm;

  return (
    <form className={formClasses} onSubmit={onSubmit}>
      <div
        className={css.publicReviewRatingGroup}
        aria-label="Rating"
        onMouseLeave={() => setHoveredRating(0)}
      >
        {[1, 2, 3, 4, 5].map(value => {
          const isActive = value <= (hoveredRating || rating);
          const ratingClasses = isActive
            ? `${css.publicReviewRating} ${css.publicReviewRatingActive}`
            : css.publicReviewRating;

          return (
            <button
              key={value}
              type="button"
              className={ratingClasses}
              onClick={() => setRating(value)}
              onMouseEnter={() => setHoveredRating(value)}
              aria-label={`${value} star${value === 1 ? '' : 's'}`}
              aria-pressed={value === rating}
            >
              <Icons name={isActive ? 'starFilled' : 'starOutline'} />
            </button>
          );
        })}
      </div>
      <textarea
        className={css.publicReviewComment}
        value={comment}
        onChange={event => setComment(event.target.value)}
        placeholder="Share what other boaters should know"
        rows={4}
      />
      <div className={css.publicReviewUploadRow}>
        <DropZone
          className={css.publicReviewDropzone}
          onAttachmentUpload={onAttachmentUpload}
          label="Add photos"
        />
        {uploadInProgress ? (
          <span className={css.publicReviewUploadSpinner}>
            <IconSpinner />
          </span>
        ) : null}
      </div>
      {photos.length > 0 ? (
        <div className={css.publicReviewPhotos}>
          {photos.map(photo => (
            <button
              key={`${photo.url}-${photo.name}`}
              type="button"
              className={css.publicReviewPhoto}
              onClick={() => setPhotos(existingPhotos => existingPhotos.filter(p => p !== photo))}
              aria-label={`Remove ${photo.name || 'review photo'}`}
            >
              <img src={photo.url} alt={photo.name || 'Review upload'} />
            </button>
          ))}
        </div>
      ) : null}
      {error ? (
        <p className={css.publicReviewError} role="alert">
          {error}
        </p>
      ) : null}
      <PrimaryButton
        className={css.publicReviewSubmit}
        type="submit"
        inProgress={submitInProgress}
        disabled={submitInProgress || uploadInProgress}
      >
        Submit review
      </PrimaryButton>
    </form>
  );
};
const sliderSettings = {
  dots: false,
  infinite: true,
  autoplay: false,
  autoplaySpeed: 2000,
  speed: 500,
  slidesToShow: 2, // Display 4 items on desktop
  slidesToScroll: 1, // Scroll 1 item at a time
  variableWidth: false,
  focusOnSelect: false,
  centerMode: false,
  nextArrow: <RenderNextArrow />,
  prevArrow: <RenderPrevArrow />,
  responsive: [
    {
      breakpoint: 1024, // Adjust settings for smaller viewports
      settings: {
        slidesToShow: 2,
        variableWidth: false,
      },
    },
    {
      breakpoint: 768, // Adjust settings for smaller viewports
      settings: {
        slidesToShow: 2,
        variableWidth: false,
      },
    },
    {
      breakpoint: 480, // Adjust settings for smaller viewports
      settings: {
        slidesToShow: 1,
        variableWidth: false,
      },
    },
  ],
};
const SectionReviews = props => {
  const {
    reviews,
    imageReviews,
    fetchReviewsError,
    onManageDisableScrolling,
    listing,
    currentUser,
    isAuthenticated,
    onUploadPublicReviewImage,
    onReviewCreated,
  } = props;

  // Now call mergeReviews function with your arrays to get the merged array
  const mergedReviews = mergeReviews(reviews, imageReviews);
  const reviewsForDisplay = isArrayLength(mergedReviews) ? mergedReviews : reviews;
  const hasVisibleReviews = isArrayLength(reviewsForDisplay);
  const isPublicMoorage = listing?.attributes?.publicData?.infoOnly === true;

  return (
    <section className={css.sectionReviews}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.reviewsTitle" values={{ count: reviews.length }} />
      </Heading>
      {fetchReviewsError ? (
        <H2 className={css.errorText}>
          <FormattedMessage id="ListingPage.reviewsError" />
        </H2>
      ) : null}
      {hasVisibleReviews ? (
        <Reviews onManageDisableScrolling={onManageDisableScrolling} reviews={reviewsForDisplay} />
      ) : null}
      {isPublicMoorage && isAuthenticated ? (
        <PublicListingReviewForm
          listing={listing}
          reviews={reviews}
          currentUser={currentUser}
          hasVisibleReviews={hasVisibleReviews}
          onUploadPublicReviewImage={onUploadPublicReviewImage}
          onReviewCreated={onReviewCreated}
        />
      ) : null}
    </section>
  );
};

export default SectionReviews;
